import { Box, Button, Icon, Typography } from "@outschool/backpack";
import { MainToolbarPinnedTabs } from "@outschool/gql-backend-generated";
import { faChevronDown, faChevronUp } from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import {
  leaderPath,
  orgTeacherAvailabilityPath,
  sellerOrgPath,
  teachTipsUrl,
  teacherDashboardPath,
  teacherSchedulePath
} from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import { ExternalLink, NavTabs } from "@outschool/ui-components-shared";
import { union } from "lodash";
import React, { useState } from "react";

import * as Routes from "../../../shared/Routes";
import * as User from "../../../shared/User";
import { UserMode } from "../../../shared/UserMode";
import { useFeatureFlag } from "../../lib/FeatureFlags";
import Link from "../Link";
import Toolbar from "../Toolbar";
import UnreadMessagesCount from "../UnreadMessagesCount";
import { PopoverMenu } from "./DesktopHeaderNav";
import { HeaderMenuItem, HeaderMenuSeparator } from "./HeaderMenu";

const ConversationBadge = () => {
  const { currentUser } = useSession();

  return (
    <UnreadMessagesCount
      color="red"
      count={User.unreadConversationsCount(currentUser)}
    />
  );
};

type MainTabProps = NavTabs.TabProps & { badge?: () => JSX.Element };

export const MAX_PINNED_TABS = 3;

export const AVAILABLE_PROFILE_TABS: Map<MainToolbarPinnedTabs, MainTabProps> =
  new Map([
    [
      MainToolbarPinnedTabs.Conversations,
      {
        badge: ConversationBadge,
        label: "Conversations",
        to: Routes.conversationsPath(UserMode.Educator),
        exact: false
      }
    ],
    [
      MainToolbarPinnedTabs.Classes,
      {
        label: "Classes",
        to: Routes.teacherClassesPath(),
        matchPaths: [
          Routes.teacherClassesPath(),
          Routes.activityBasePath(false)
        ],
        exact: false
      }
    ],
    [
      MainToolbarPinnedTabs.Schedule,
      {
        label: "Schedule",
        to: teacherSchedulePath()
      }
    ],
    [
      MainToolbarPinnedTabs.Insights,
      {
        label: "Insights",
        to: Routes.teacherStatsPath()
      }
    ],
    [
      MainToolbarPinnedTabs.Sections,
      {
        label: "Sections",
        to: Routes.teacherSectionsPath()
      }
    ],
    [
      MainToolbarPinnedTabs.Availability,
      {
        label: "Group class availability",
        to: Routes.teacherAvailabilityPath()
      }
    ],
    [
      MainToolbarPinnedTabs.Coupons,
      {
        label: "Coupons",
        to: Routes.teacherCouponsPath()
      }
    ],
    [
      MainToolbarPinnedTabs.Profile,
      {
        label: "Profile",
        to: Routes.leaderSettingsPath()
      }
    ],
    [
      MainToolbarPinnedTabs.Marketing,
      {
        label: "Marketing",
        to: Routes.teacherMarketingPath()
      }
    ],
    [
      MainToolbarPinnedTabs.Groups,
      {
        label: "Groups",
        to: Routes.teacherClubsPath()
      }
    ]
  ]);

/**
 * This component is hidden when Educator Mode is enabled, because it is
 * replaced by navigation items in the global header.
 */
export function TeachToolbar() {
  const { currentUser } = useSession();
  const isSellerOrg = currentUser?.sellerOrg?.currentUserIsOwner;
  if (!isSellerOrg) {
    return null;
  } else {
    return <FullTeachToolbar />;
  }
}

/**
 * Teacher navigation toolbar including a news banner and page title header.
 */
function FullTeachToolbar() {
  const { currentUser, currentUserIsLoading } = useSession();
  const { t } = useTranslation("client\\components\\nav\\TeachToolbar");

  const isOrgTeacher =
    currentUser &&
    currentUser.sellerOrg &&
    currentUser.sellerOrg.currentUserIsTeacher;

  const currentUserSellerOrgName = isOrgTeacher && currentUser?.sellerOrg?.name;
  const titleContent = isOrgTeacher ? (
    <Trans t={t}>
      Teaching{" "}
      <Typography
        variant="inherit"
        sx={{
          marginLeft: "0.25em"
        }}
      >
        for{" "}
        <Link
          to={leaderPath(
            // @ts-ignore TS(2531): Object is possibly 'null'.
            currentUser.sellerOrg.uid,
            // @ts-ignore TS(2531): Object is possibly 'null'.
            currentUser.sellerOrg.leader_link
          )}
        >
          {{ currentUserSellerOrgName }}
        </Link>
      </Typography>
    </Trans>
  ) : (
    <>{t("Teaching")}</>
  );
  return (
    <>
      <Toolbar>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: "1024px",
            margin: "0 auto",
            padding: "0 1em"
          }}
        >
          <Typography
            variant="h2"
            sx={{
              marginBottom: "0.5em"
            }}
          >
            {titleContent}
          </Typography>
          {!currentUserIsLoading && (
            <TeachToolbarItems currentUser={currentUser} />
          )}
        </Box>
      </Toolbar>
    </>
  );
}

/**
 * Toolbar of teacher navigation items: links to several teacher-specific pages.
 */
export function TeachToolbarItems({ currentUser }) {
  const isSellerOrg = currentUser?.sellerOrg?.currentUserIsOwner;
  const isOrgTeacher = currentUser?.sellerOrg?.currentUserIsTeacher;
  const showManageTabs = !isSellerOrg && !isOrgTeacher;

  const { t } = useTranslation("client\\components\\nav\\TeachToolbar");

  return (
    <NavTabs.TabList>
      {!isSellerOrg && !isOrgTeacher && (
        <NavTabs.Tab label={t("Dashboard")} to={teacherDashboardPath()} />
      )}
      {User.hasTeacherApplication(currentUser) && (
        <NavTabs.Tab label={t("Apply")} to={Routes.teacherApplyPath()} />
      )}
      <NavTabs.Tab label={t("Schedule")} to={teacherSchedulePath()} />
      <NavTabs.Tab label={t("Sections")} to={Routes.teacherSectionsPath()} />
      {showManageTabs && (
        <React.Fragment>
          <NavTabs.Tab label={t("Classes")} to={Routes.teacherClassesPath()} />
          <NavTabs.Tab label={t("Groups")} to={Routes.teacherClubsPath()} />
          <NavTabs.Tab label={t("Profile")} to={Routes.leaderSettingsPath()} />
          <NavTabs.Tab
            label={t("Group class availability")}
            to={Routes.teacherAvailabilityPath()}
          />
          {currentUser && currentUser.leader_subscribed_at && (
            <NavTabs.Tab
              label={t("Stats")}
              to={Routes.teacherStatsPath()}
              matchPaths={[
                Routes.teacherStatsPath(),
                Routes.teacherFeedbackPath(),
                Routes.sellerOrgStatsPath(),
                Routes.sellerOrgFeedbackPath()
              ]}
            />
          )}
        </React.Fragment>
      )}
      {!isOrgTeacher && (
        <NavTabs.Tab
          label={t("Coupons")}
          to={Routes.teacherCouponsPath()}
          key="coupons"
          exact={false}
        />
      )}
      <NavTabs.Tab
        label={t("Marketing")}
        to={Routes.teacherMarketingPath()}
        key="marketing"
        exact={false}
      />
    </NavTabs.TabList>
  );
}

export const EducatorModeToolbar = () => {
  const { currentUser } = useSession();
  const isOrgTeacher = currentUser?.sellerOrg?.currentUserIsTeacher;
  const isOrgApplicant = !!currentUser?.latestSellerOrgApplication;
  const menuTabs = union(
    currentUser?.leader?.mainToolbarPinnedTabs,
    Array.from(AVAILABLE_PROFILE_TABS.keys())
    // @ts-ignore TS(2345): Argument of type 'MainToolbarPinnedTabs | null' is... Remove this comment to see the full error message
  ).filter(tab => Object.values(MainToolbarPinnedTabs).includes(tab));
  const pinnedTabs = menuTabs.splice(0, MAX_PINNED_TABS);

  if (isOrgTeacher) {
    return <OrgEducatorToolbar />;
  } else {
    return (
      <NavTabs.TabList sx={{ marginTop: 0 }}>
        <ToolbarTab label="Home" to={teacherDashboardPath()} />
        {User.hasTeacherApplication(currentUser) && (
          <ToolbarTab label="Apply" to={Routes.teacherApplyPath()} />
        )}
        {pinnedTabs.map(tab => {
          // @ts-ignore TS(2345): Argument of type 'MainToolbarPinnedTabs | null' is... Remove this comment to see the full error message
          const tabInfo = AVAILABLE_PROFILE_TABS.get(tab);

          // @ts-ignore TS(2532): Object is possibly 'undefined'.
          return <ToolbarTab key={`${tabInfo.label}`} {...tabInfo} />;
        })}
        {isOrgApplicant && (
          <ToolbarTab label="Manage" to={sellerOrgPath()} exact={false} />
        )}
        <EducatorModeMenu tabOrder={menuTabs as MainToolbarPinnedTabs[]} />
      </NavTabs.TabList>
    );
  }
};

const ToolbarTab = props => {
  const { badge, ...rest } = props;
  const { currentUser } = useSession();

  return (
    <EducatorTab {...rest}>
      {!!badge && (
        <>
          {`${rest.label} `}
          <UnreadMessagesCount
            color="red"
            count={User.unreadConversationsCount(currentUser)}
          />
        </>
      )}
    </EducatorTab>
  );
};

const EducatorTab = (props: NavTabs.TabProps) => {
  return (
    <NavTabs.UnderlinedTab
      linkSx={{ paddingY: "medium", marginX: "medium" }}
      {...props}
    />
  );
};

const EducatorTabExternal = (props: NavTabs.TabProps) => (
  <NavTabs.UnderlinedTabExternal
    linkSx={{ paddingY: "medium", marginX: "medium" }}
    {...props}
  />
);

const OrgEducatorToolbar = () => {
  const useOneOnOneFormatForOrgs = useFeatureFlag(
    "supply-one-on-one-activity-format-for-orgs"
  );
  const conversationProps = AVAILABLE_PROFILE_TABS.get(
    MainToolbarPinnedTabs.Conversations
  );
  const groupsProps = AVAILABLE_PROFILE_TABS.get(MainToolbarPinnedTabs.Groups);

  return (
    <NavTabs.TabList sx={{ marginTop: 0, alignItems: "baseline", flex: 3 }}>
      <EducatorTab label="Schedule" to={teacherSchedulePath()} />
      <EducatorTab label="Sections" to={Routes.teacherSectionsPath()} />
      {useOneOnOneFormatForOrgs && (
        <EducatorTab
          label="1-on-1 Availability"
          to={orgTeacherAvailabilityPath()}
        />
      )}
      {/* @ts-ignore TS(2322): Type '{ sx?: object | undefined; linkSx?: SystemSt... Remove this comment to see the full error message */}
      <EducatorTab {...groupsProps} />
      <ToolbarTab {...conversationProps} />
      <EducatorTabExternal label="Educator Library" to={teachTipsUrl()} />
    </NavTabs.TabList>
  );
};

const EducatorModeMenu = ({
  tabOrder
}: {
  tabOrder: MainToolbarPinnedTabs[];
}) => {
  const buttonRef = React.useRef();
  const [open, setOpen] = useState(false);
  // This component is structured similarly to UnderlinedTab, but cannot use it
  // directly because UnderlinedTab is always a link component. Here, we need a
  // button instead.
  return (
    <PopoverMenu
      open={open}
      setOpen={setOpen}
      // @ts-ignore TS(2322): Type 'MutableRefObject<undefined>' is not assignab... Remove this comment to see the full error message
      buttonRef={buttonRef}
      renderButton={(ref: any, buttonRef: any) => (
        <li role="presentation" ref={buttonRef}>
          <Button
            variant="link"
            ref={ref}
            onClick={() => setOpen(!open)}
            sx={{
              paddingY: "1em",
              marginX: "1em",
              color: "grey.500",
              lineHeight: 1.5,
              marginBottom: "4px",
              fontWeight: "regular"
            }}
            endIcon={<Icon icon={open ? faChevronUp : faChevronDown} />}
          >
            Menu
          </Button>
        </li>
      )}
    >
      <EducatorMenuItems onClose={() => setOpen(false)} tabOrder={tabOrder} />
    </PopoverMenu>
  );
};

const EducatorMenuItems = ({
  onClose,
  tabOrder
}: {
  onClose: () => void;
  tabOrder: MainToolbarPinnedTabs[];
}) => {
  const MenuItem = ({ to, label, badge = undefined }) => {
    const isActive = window.location.pathname.startsWith(to);
    const { currentUser } = useSession();

    return (
      <Link onClick={onClose} to={to}>
        <HeaderMenuItem label={label} sx={isActive ? { color: "blue2" } : {}}>
          {!!badge && (
            <UnreadMessagesCount
              color="red"
              sx={{ marginLeft: "0.5em" }}
              count={User.unreadConversationsCount(currentUser)}
            />
          )}
        </HeaderMenuItem>
      </Link>
    );
  };

  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        paddingY: "1em"
      }}
    >
      {tabOrder.map(tab => {
        const tabInfo = AVAILABLE_PROFILE_TABS.get(tab);
        // @ts-ignore TS(2339): Property 'label' does not exist on type 'MainTabPr... Remove this comment to see the full error message
        const { label, to, badge } = tabInfo;

        return <MenuItem key={label} label={label} to={to} badge={badge} />;
      })}
      <HeaderMenuSeparator />
      <MenuItem label="Create new class" to={Routes.activityCreatePath()} />
      <ExternalLink onClick={onClose} url={teachTipsUrl()}>
        <HeaderMenuItem label="Educator Library" />
      </ExternalLink>
    </Box>
  );
};
